import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { withProtectedRoute } from "../../components/ProtectedRoute";
import {
    MediaMigrationEvent,
    MediaMigrationDiagnostics,
    MediaMigrationItem,
    MediaMigrationRun,
    MigrationEventLevel,
    mediaMigrationService,
} from "../../services/MediaMigrationService";

type Action = "start" | "stop" | "resume";
type EventFilter = "all" | "warning" | "error";
const activeStatuses = new Set(["discovering", "running", "stopping"]);

const formatBytes = (bytes: number) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index > 2 ? 2 : 1)} ${units[index]}`;
};

const formatDuration = (seconds: number | null) => {
    if (seconds === null) return "Calculating";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours) return `${hours}h ${minutes}m`;
    if (minutes) return `${minutes}m`;
    return `${Math.max(0, seconds)}s`;
};

const statusLabel = (status: string) =>
    status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const requestErrorMessage = (requestError: unknown, fallback: string) => {
    if (axios.isAxiosError(requestError)) {
        const detail = (requestError.response?.data as { detail?: string } | undefined)?.detail;
        if (detail) return detail;
        if (!requestError.response) {
            return `${fallback} The request did not receive a response from the API.`;
        }
    }
    return fallback;
};

export const MediaMigrationPageComponent = () => {
    const [run, setRun] = useState<MediaMigrationRun | null>(null);
    const [failedItems, setFailedItems] = useState<MediaMigrationItem[]>([]);
    const [events, setEvents] = useState<MediaMigrationEvent[]>([]);
    const [diagnostics, setDiagnostics] = useState<MediaMigrationDiagnostics | null>(null);
    const [eventFilter, setEventFilter] = useState<EventFilter>("all");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [action, setAction] = useState<Action | null>(null);
    const [password, setPassword] = useState("");

    const refresh = useCallback(async () => {
        try {
            const current = await mediaMigrationService.getCurrent();
            setRun(current);
            if (current) {
                const [nextFailedItems, nextEvents] = await Promise.all([
                    current.failed_records ? mediaMigrationService.getFailedItems(current.id) : Promise.resolve([]),
                    mediaMigrationService.getEvents(
                        current.id,
                        eventFilter === "all" ? undefined : (eventFilter as MigrationEventLevel),
                    ),
                ]);
                setFailedItems(nextFailedItems);
                setEvents(nextEvents);
            } else {
                setFailedItems([]);
                setEvents([]);
            }
            setError(null);
        } catch (requestError) {
            setError("Migration status could not be loaded.");
        } finally {
            setLoading(false);
        }
    }, [eventFilter]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const isActive = !!run && activeStatuses.has(run.status);

    useEffect(() => {
        const delay = isActive ? 3000 : 15000;
        const timer = window.setInterval(() => void refresh(), delay);
        return () => window.clearInterval(timer);
    }, [refresh, isActive]);

    const openConfirmation = (nextAction: Action) => {
        setPassword("");
        setError(null);
        setAction(nextAction);
    };

    const loadDiagnostics = useCallback(async () => {
        setDiagnosticsLoading(true);
        try {
            setDiagnostics(await mediaMigrationService.getDiagnostics());
        } catch (requestError) {
            setError(requestErrorMessage(requestError, "Migration diagnostics could not be loaded."));
        } finally {
            setDiagnosticsLoading(false);
        }
    }, []);

    const submitAction = async () => {
        if (!action || !password || (action !== "start" && !run)) return;
        setSubmitting(true);
        try {
            let updated: MediaMigrationRun;
            if (action === "start") {
                updated = await mediaMigrationService.start(password);
            } else if (run && action === "stop") {
                updated = await mediaMigrationService.stop(run.id, password);
            } else if (run) {
                updated = await mediaMigrationService.resume(run.id, password);
            } else {
                return;
            }
            setRun(updated);
            setAction(null);
            setPassword("");
            setError(null);
        } catch (requestError) {
            setError(requestErrorMessage(requestError, "The migration action could not be completed."));
            setAction(null);
            void loadDiagnostics();
        } finally {
            setSubmitting(false);
        }
    };

    const canStart = !run || run.status === "completed";
    const canStop = !!run && ["discovering", "running"].includes(run.status);
    const canResume = !!run && ["paused", "completed_with_errors", "failed"].includes(run.status);
    const processed = run ? run.completed_records + run.failed_records : 0;

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 5 }, pl: { md: 14 }, pr: { xs: 2, md: 5 } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                <CloudSyncIcon color="action" />
                <Typography variant="h4" component="h1">
                    Media Migration
                </Typography>
                {run && <Chip label={statusLabel(run.status)} size="small" variant="outlined" />}
            </Stack>
            <Typography color="text.secondary" mb={4}>
                Transfer job media from Cloudinary to private S3 storage.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}
            {run?.error_message && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {run.error_message}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" py={10}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Box component="section" aria-label="Migration progress" mb={4}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={2}
                            mb={2}>
                            <Box>
                                <Typography variant="h6">Progress</Typography>
                                <Typography color="text.secondary">
                                    {run
                                        ? `${processed.toLocaleString()} of ${run.total_records.toLocaleString()} records processed`
                                        : "No migration has been started."}
                                </Typography>
                            </Box>
                            <Typography variant="h5" fontWeight={600}>
                                {run?.percentage ?? 0}%
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant={run?.status === "discovering" ? "indeterminate" : "determinate"}
                            value={run?.percentage ?? 0}
                            color={run?.failed_records ? "warning" : "primary"}
                            sx={{ height: 10, borderRadius: 1 }}
                        />
                    </Box>

                    <Divider />
                    <Box component="section" py={4}>
                        <Box display="grid" gridTemplateColumns={{ xs: "1fr 1fr", md: "repeat(4, 1fr)" }} gap={3}>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Transferred
                                </Typography>
                                <Typography variant="h6">{formatBytes(run?.completed_bytes ?? 0)}</Typography>
                            </Box>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Total size
                                </Typography>
                                <Typography variant="h6">{formatBytes(run?.total_bytes ?? 0)}</Typography>
                            </Box>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    ETA
                                </Typography>
                                <Typography variant="h6">{formatDuration(run?.eta_seconds ?? null)}</Typography>
                            </Box>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Throughput
                                </Typography>
                                <Typography variant="h6">
                                    {formatBytes(run?.throughput_bytes_per_second ?? 0)}/s
                                </Typography>
                            </Box>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Completed
                                </Typography>
                                <Typography variant="h6">{run?.completed_records.toLocaleString() ?? 0}</Typography>
                            </Box>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Failed
                                </Typography>
                                <Typography variant="h6" color={run?.failed_records ? "error.main" : "text.primary"}>
                                    {run?.failed_records.toLocaleString() ?? 0}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Elapsed
                                </Typography>
                                <Typography variant="h6">{formatDuration(run?.elapsed_seconds ?? 0)}</Typography>
                            </Box>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Last updated
                                </Typography>
                                <Typography variant="body1">
                                    {run ? new Date(run.updated_datetime).toLocaleString() : "Not started"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Divider />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} py={4}>
                        {canStart && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<WarningAmberIcon />}
                                onClick={() => openConfirmation("start")}>
                                Start Migration
                            </Button>
                        )}
                        {canStop && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<PauseCircleOutlineIcon />}
                                onClick={() => openConfirmation("stop")}>
                                Stop Migration
                            </Button>
                        )}
                        {canResume && run?.status !== "completed" && (
                            <Button
                                variant="contained"
                                startIcon={<PlayArrowIcon />}
                                onClick={() => openConfirmation("resume")}>
                                Resume Migration
                            </Button>
                        )}
                        <Button variant="outlined" disabled={diagnosticsLoading} onClick={() => void loadDiagnostics()}>
                            {diagnosticsLoading ? "Checking..." : "Run Diagnostics"}
                        </Button>
                    </Stack>

                    {diagnostics && (
                        <Box component="section" pb={4}>
                            <Typography variant="h6" mb={2}>
                                Diagnostics
                            </Typography>
                            <Box
                                display="grid"
                                gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }}
                                gap={2}
                                mb={2}>
                                <Box>
                                    <Typography color="text.secondary" variant="body2">
                                        Environment
                                    </Typography>
                                    <Typography variant="body1">{diagnostics.environment || "Unknown"}</Typography>
                                </Box>
                                <Box>
                                    <Typography color="text.secondary" variant="body2">
                                        Media S3 bucket
                                    </Typography>
                                    <Typography variant="body1">
                                        {diagnostics.media_s3_bucket_configured
                                            ? diagnostics.media_s3_bucket
                                            : "Not configured"}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography color="text.secondary" variant="body2">
                                        Cloudinary records
                                    </Typography>
                                    <Typography variant="body1">
                                        {diagnostics.cloudinary_attachment_count === null
                                            ? "Unknown"
                                            : diagnostics.cloudinary_attachment_count.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>

                            {diagnostics.errors.length > 0 && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {diagnostics.errors.join(" ")}
                                </Alert>
                            )}

                            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                                <Table size="small" aria-label="Migration diagnostics">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Check</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Rows</TableCell>
                                            <TableCell>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(diagnostics.tables).map(([tableName, tableStatus]) => (
                                            <TableRow key={tableName}>
                                                <TableCell>{tableName}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={tableStatus.exists ? "success" : "error"}
                                                        label={tableStatus.exists ? "Present" : "Missing"}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {tableStatus.count === null
                                                        ? "-"
                                                        : tableStatus.count.toLocaleString()}
                                                </TableCell>
                                                <TableCell>{tableStatus.error || "-"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {failedItems.length > 0 && (
                        <Box component="section" pt={1}>
                            <Typography variant="h6" mb={2}>
                                Recent failures
                            </Typography>
                            <Stack divider={<Divider flexItem />}>
                                {failedItems.map((item) => (
                                    <Box key={item.id} py={1.5}>
                                        <Typography fontWeight={600}>Attachment {item.attachment_id}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.error_message || "Unknown migration error"}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {run && (
                        <Box component="section" pt={4}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                                spacing={2}
                                mb={2}>
                                <Box>
                                    <Typography variant="h6">Migration activity</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Latest lifecycle, transfer, retry, and failure events
                                    </Typography>
                                </Box>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={eventFilter}
                                    onChange={(_, value: EventFilter | null) => value && setEventFilter(value)}
                                    aria-label="Migration event filter">
                                    <ToggleButton value="all">All</ToggleButton>
                                    <ToggleButton value="warning">Warnings</ToggleButton>
                                    <ToggleButton value="error">Errors</ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>

                            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                                <Table size="small" aria-label="Migration activity log">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Time</TableCell>
                                            <TableCell>Level</TableCell>
                                            <TableCell>Activity</TableCell>
                                            <TableCell>Attachment</TableCell>
                                            <TableCell align="right">Bytes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {events.length ? (
                                            events.map((event) => (
                                                <TableRow key={event.id} hover>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                        {new Date(event.created_datetime).toLocaleString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={statusLabel(event.level)}
                                                            color={
                                                                event.level === "error"
                                                                    ? "error"
                                                                    : event.level === "warning"
                                                                    ? "warning"
                                                                    : event.level === "success"
                                                                    ? "success"
                                                                    : "default"
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{event.message}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {statusLabel(event.event_type)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{event.attachment_id || "Run"}</TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                        {event.bytes_transferred === null
                                                            ? "-"
                                                            : formatBytes(event.bytes_transferred)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                    No activity matches this filter.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </>
            )}

            <Dialog open={action !== null} onClose={() => !submitting && setAction(null)} fullWidth maxWidth="xs">
                <DialogTitle>{action ? `${statusLabel(action)} migration` : "Confirm migration action"}</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" mb={2}>
                        Enter your administrator password to confirm this action.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Password"
                        type="password"
                        value={password}
                        autoComplete="current-password"
                        onChange={(event) => setPassword(event.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && password && void submitAction()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAction(null)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={action === "stop" ? "error" : action === "start" ? "warning" : "primary"}
                        disabled={!password || submitting}
                        onClick={() => void submitAction()}>
                        {submitting ? <CircularProgress size={20} color="inherit" /> : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export const MediaMigrationPage = withProtectedRoute(MediaMigrationPageComponent, { requiresAdmin: true });
